/**
 * Thin fetch wrapper for the three public magic-link endpoints the
 * customer-facing /welcome routes use (initial register). All three are
 * anonymous (no session) so requests are sent with `credentials: 'omit'`.
 * The kernel response envelope `{ success, data, error }` is unwrapped to
 * `data`, matching `kernel_client.ts`.
 */
import { api_base } from './kernel_client';

export type InitialRegisterBody = {
  firstname: string;
  lastname: string;
  birthdate: string;
  checkin: string;
};

export type VerifyMagicLinkResponse = {
  valid: boolean;
  purpose: string;
  username?: string;
};

export type SetPasswordBody = {
  token: string;
  password: string;
  password_repeat: string;
};

/**
 * Error thrown by the magic-link endpoints. `message` stays the kernel error
 * code so existing `e.message === 'BAD_REQUEST'` checks keep working, while
 * `details` carries the kernel's field-level messages — for a password policy
 * rejection that is the configured rule text, which the UI shows verbatim so it
 * always matches what the kernel actually enforces.
 */
export class MagicLinkError extends Error {
  /** Kernel error code, e.g. `VALIDATION_ERROR` or `BAD_REQUEST`. */
  readonly code: string;

  /** Field-level messages from the kernel's `error.details`, in order. */
  readonly details: string[];

  constructor(code: string, details: string[]) {
    super(code);
    this.name = 'MagicLinkError';
    this.code = code;
    this.details = details;
  }
}

/**
 * Flattens the kernel's `error.details` into plain messages.
 *
 * The REST envelope returns `[{ <fieldname>: <message> }]`, while the legacy
 * entity route returns `[{ tablename, fieldname, message }]`. Both are accepted.
 *
 * @param details - The raw `error.details` value.
 * @returns The messages found, in order.
 */
function read_detail_messages(details: unknown): string[] {
  if (Array.isArray(details) === false) return [];

  const messages: string[] = [];

  for (const entry of details as unknown[]) {
    if (typeof entry === 'string' && entry !== '') {
      messages.push(entry);
      continue;
    }

    if (entry === null || typeof entry !== 'object') continue;

    const record = entry as Record<string, unknown>;

    if (typeof record['message'] === 'string' && record['message'] !== '') {
      messages.push(record['message']);
      continue;
    }

    for (const value of Object.values(record)) {
      if (typeof value === 'string' && value !== '') messages.push(value);
    }
  }

  return messages;
}

async function send<T>(path: string, method: 'POST' | 'GET' | 'PUT', body: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const init: RequestInit = { method, headers, credentials: 'omit' };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  const response = await fetch(api_base() + path, init);
  if (response.ok === false) {
    let detail = '';
    let messages: string[] = [];
    try {
      const parsed = await response.json();
      detail =
        (parsed?.error?.code as string | undefined) ??
        (parsed?.error?.message as string | undefined) ??
        '';
      messages = read_detail_messages(parsed?.error?.details);
    } catch {
      // ignore — non-json error body
    }
    throw new MagicLinkError(
      detail !== '' ? detail : 'KERNEL_HTTP_' + String(response.status),
      messages
    );
  }
  const wire = await response.json();
  if (wire && typeof wire === 'object' && 'data' in wire) {
    return wire.data as T;
  }
  return wire as T;
}

/**
 * POST /auth/initial-register — request the initial-register email.
 * Always resolves 200 (anti-enumeration) for a well-formed body.
 *
 * @param body - The customer's identifying personal data.
 */
export async function request_initial_register(body: InitialRegisterBody): Promise<null> {
  return send<null>('/auth/initial-register', 'POST', body);
}

/**
 * GET /auth/verify-magic-link?token=… — validate the token from the email
 * link. For initial_register the token is NOT consumed here.
 *
 * @param token - The raw token from the URL.
 */
export async function verify_magic_link(token: string): Promise<VerifyMagicLinkResponse> {
  return send<VerifyMagicLinkResponse>(
    '/auth/verify-magic-link?token=' + encodeURIComponent(token),
    'GET',
    undefined
  );
}

/**
 * PUT /auth/reset-password — set the first password and consume the token.
 *
 * @param body - The token plus the new password and its repeat.
 */
export async function set_password(body: SetPasswordBody): Promise<null> {
  return send<null>('/auth/reset-password', 'PUT', body);
}
